import type { Ref, SelectHTMLAttributes } from 'react'
import { useHitTarget, type HitTargetSize } from '../responsive/hitTarget'
import './select.css'

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  /** React 19 ref-as-prop, forwarded to the `<select>` so a caller can move focus to it. */
  ref?: Ref<HTMLSelectElement>
  /** The accessible name. Required: a closed select shows only its current value, which names nothing. */
  label: string
  /**
   * The control's hit-target floor: 33px `page` by default, 44px on a phone
   * either way. `form` is `Field`'s form density (`padding: 12px 14px`, the
   * larger type), so a select in a form row is the height of the inputs beside
   * it. The size is also the modifier class, `select__control--<size>`; only
   * `form` has a rule, as only `field__input--form` does.
   */
  size?: Extract<HitTargetSize, 'page' | 'panel' | 'form'>
}

/**
 * A native `<select>` in the app's field skin — the filter row's control.
 *
 * Native on purpose. The list it opens is the platform's own, which is what
 * makes it operable by keyboard, by screen reader and by thumb for free; a
 * custom listbox would have to re-earn all three. Only the closed state is
 * skinned, and the OS arrow is replaced by a drawn chevron in a token colour,
 * since a native arrow carries no declaration a contrast sweep can read.
 *
 * **Not a re-export of `RuleSelect`, and `RuleSelect` is unchanged.** The plan
 * promoted one into the other. `RuleSelect`'s skin is `--surface`/`--edge`/
 * `--ink`, and a registered control may name none of the surface-relative inks
 * (`controlTokens.test.ts`): repointing M11's builder at this would restyle it.
 * Migrating it is its own change, with its own contrast sweep.
 *
 * The floor is inline, so a caller's stylesheet cannot shrink it under the
 * target (M0-R29, issue 90).
 */
export function Select({ label, size = 'page', className, style, children, ...rest }: Props) {
  const minHeight = useHitTarget(size)
  return (
    <span className={['select', className].filter(Boolean).join(' ')}>
      <select className={`select__control select__control--${size}`} aria-label={label} style={{ ...style, minHeight }} {...rest}>
        {children}
      </select>
    </span>
  )
}
